import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Typography, TextField, Box, FormControlLabel, Switch,
  IconButton, Tooltip, Button,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

import SectionCard from './SectionCard';
import HelpTip from '../../common/HelpTip';
import { GroupLabel } from '../tabLayout';
import { Fact, FactGrid, OnOffPill, QuietNote, numSx } from './sectionParts';
import { AdmissionCycle } from '../../../types/admissions';

const NUMBERING_HELP = {
  title: 'Roll number counter',
  terms: [
    ['Restarts each cycle', 'the first card of every cycle is number 1.'],
    ['Continues across cycles', 'numbers carry on from the last cycle, so none is ever reused.'],
  ] as const,
};

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  /** Why the numbering is fixed on this open cycle (cards already issued).
   *  The instructions stay editable. */
  numberingLockedReason?: string | null;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * AdmitCardSection — roll-number format + per-cycle instructions printed on
 * every admit card. The actual rendering lives in pdf/AdmitCardDocument.tsx;
 * this editor just tunes the inputs.
 *
 * Only shown as "editable" when stages.admitCardEnabled is true — otherwise
 * the section is still visible as read-only hint ("Enable admit cards first").
 */

const DEFAULT_FORMAT = {
  prefix: 'AC',
  includeYear: true,
  includeClassCode: false,
  padding: 5,
  perCycleCounter: true,
};

const AdmitCardSection: React.FC<Props> = ({ cycle, canEdit, numberingLockedReason, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    format: cycle.admitCardNumberFormat || DEFAULT_FORMAT,
    instructions: cycle.admitCardInstructions || [],
  });

  useEffect(() => {
    if (editing) {
      setDraft({
        format: cycle.admitCardNumberFormat || DEFAULT_FORMAT,
        instructions: cycle.admitCardInstructions || [],
      });
    }
  }, [editing, cycle.admitCardNumberFormat, cycle.admitCardInstructions]);

  // Numbering and instructions are the cycle's; which classes get cards is each pathway's call.
  const stageEnabled = (cycle.pathways || []).some(p => p.stages?.admitCardEnabled);

  const sampleRoll = useMemo(() => {
    const parts: string[] = [];
    const f = (editing ? draft.format : cycle.admitCardNumberFormat) || DEFAULT_FORMAT;
    if (f.prefix) parts.push(f.prefix);
    if (f.includeYear) parts.push(String(new Date().getFullYear()));
    if (f.includeClassCode) parts.push('G5');
    const padded = String(1).padStart(Math.max(1, f.padding || 5), '0');
    return `${parts.join('-')}-${padded}`;
  }, [editing, draft.format, cycle.admitCardNumberFormat]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // The numbering is sent only when it changed, so instructions can be
      // updated where the numbering is fixed (cards issued, cycle closed).
      const numberingChanged = JSON.stringify(draft.format) !== JSON.stringify(cycle.admitCardNumberFormat);
      await onSave({
        ...(numberingChanged ? { admitCardNumberFormat: draft.format } : {}),
        admitCardInstructions: draft.instructions.map(s => s.trim()).filter(Boolean),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const addInstruction = () => setDraft(d => ({ ...d, instructions: [...d.instructions, ''] }));
  const updateInstruction = (idx: number, v: string) =>
    setDraft(d => ({ ...d, instructions: d.instructions.map((x, i) => i === idx ? v : x) }));
  const removeInstruction = (idx: number) =>
    setDraft(d => ({ ...d, instructions: d.instructions.filter((_, i) => i !== idx) }));

  const effectiveFormat = (editing ? draft.format : cycle.admitCardNumberFormat) || DEFAULT_FORMAT;
  const effectiveInstructions = editing ? draft.instructions : (cycle.admitCardInstructions || []);

  return (
    <SectionCard
      title="Admit card format"
      description={`How roll numbers are built, and the instructions printed on every admit card${(cycle.pathways || []).length > 1 ? ' — the same for every pathway' : ''}.`}
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={handleSave}
      onCancel={() => setDraft({
        format: cycle.admitCardNumberFormat || DEFAULT_FORMAT,
        instructions: cycle.admitCardInstructions || [],
      })}
      saving={saving}
    >
      <Stack spacing={3}>
        {!stageEnabled && !editing && (
          <QuietNote>No class gets an admit card. Turn admit cards on under Stages.</QuietNote>
        )}

        {editing && numberingLockedReason && <QuietNote>{numberingLockedReason}</QuietNote>}
        {editing && !numberingLockedReason ? (
          <Stack spacing={2}>
            <Fact label="Sample roll number">{sampleRoll}</Fact>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <TextField
                size="small"
                label="Prefix"
                value={effectiveFormat.prefix}
                onChange={(e) => setDraft(d => ({ ...d, format: { ...d.format, prefix: e.target.value } }))}
              />
              <TextField
                size="small"
                label="Padding (digits)"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={effectiveFormat.padding}
                onChange={(e) => setDraft(d => ({ ...d, format: { ...d.format, padding: Number(e.target.value) || 5 } }))}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={effectiveFormat.includeYear}
                    onChange={(_, v) => setDraft(d => ({ ...d, format: { ...d.format, includeYear: v } }))}
                  />
                }
                label="Include year"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={effectiveFormat.includeClassCode}
                    onChange={(_, v) => setDraft(d => ({ ...d, format: { ...d.format, includeClassCode: v } }))}
                  />
                }
                label="Include class code"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={effectiveFormat.perCycleCounter}
                    onChange={(_, v) => setDraft(d => ({ ...d, format: { ...d.format, perCycleCounter: v } }))}
                  />
                }
                label={<HelpTip label="Restart numbering each cycle" {...NUMBERING_HELP} />}
                sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}
              />
            </Box>
          </Stack>
        ) : (
          <FactGrid columns={3}>
            <Fact label="Sample roll number">{sampleRoll}</Fact>
            <Fact label="Prefix">{effectiveFormat.prefix || '—'}</Fact>
            <Fact label="Number length">{effectiveFormat.padding} digits</Fact>
            <Fact label="Year in roll number"><OnOffPill on={effectiveFormat.includeYear} /></Fact>
            <Fact label="Class code in roll number"><OnOffPill on={effectiveFormat.includeClassCode} /></Fact>
            <Fact label={<HelpTip label="Numbering" {...NUMBERING_HELP} />}>{effectiveFormat.perCycleCounter ? 'Restarts each cycle' : 'Continues across cycles'}</Fact>
          </FactGrid>
        )}

        <Box>
          <GroupLabel
            action={editing && (
              <Button size="small" startIcon={<Add fontSize="small" />} onClick={addInstruction}>
                Add instruction
              </Button>
            )}
          >
            Instructions on the card
          </GroupLabel>
          {effectiveInstructions.length === 0 ? (
            <QuietNote>
              No instructions yet. Common ones: "Bring the original B-Form", "Arrive 30 minutes early".
            </QuietNote>
          ) : (
            <Stack spacing={1}>
              {effectiveInstructions.map((inst, idx) => (
                editing ? (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      value={inst}
                      onChange={(e) => updateInstruction(idx, e.target.value)}
                      placeholder="Instruction text"
                    />
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => removeInstruction(idx)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Stack key={idx} direction="row" spacing={1.5} alignItems="baseline">
                    <Typography variant="body2" color="text.disabled" sx={{ ...numSx, minWidth: 16, textAlign: 'right' }}>
                      {idx + 1}.
                    </Typography>
                    <Typography variant="body2">{inst}</Typography>
                  </Stack>
                )
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </SectionCard>
  );
};

export default AdmitCardSection;
